import * as React from 'react';
import classnames from 'clsx';
import { getDataAttributes } from '@wix/editor-elements-common-utils';
import type { IMenuButtonProps } from '../MenuButton.types';
import MenuButtonLink from './MenuButtonLink';
import AccessibilityIcon from './AccessibilityIcon';
import styles from './MenuButtonRoot.scss';
import { A11Y_DROPDOWN_CHEVRON_BUTTON_ARIA_LABEL_DEFAULT } from '../constants';

const EMPTY_STRING = '\u00A0';

type MenuButtonRootProps = Partial<IMenuButtonProps> & {
  children(label: string): React.ReactNode;
};

const MenuButtonRoot: React.FC<MenuButtonRootProps> = props => {
  const {
    label,
    direction = 'ltr',
    positionInList,
    parentId,
    dataId,
    isContainer,
    isSelected,
    isHovered,
    link,
    tagName: TagName = 'div',
    id,
    className,
    compClassName,
    onClick,
    onDoubleClick,
    onMouseEnter: _onMouseEnter,
    onMouseLeave: _onMouseLeave,
    index,
    children,
    isDropDownButton,
    subItems,
  } = props;

  const [hovered, setHovered] = React.useState(false);
  const hasSubItems = subItems && subItems.length > 0;
  const isMouseEvent = (e: React.FocusEvent | React.MouseEvent) =>
    e.nativeEvent instanceof MouseEvent;
  const onMouseEnter = (e: React.MouseEvent) => {
    if (!hovered) {
      setHovered(true);
      _onMouseEnter?.(e); // Optional chaining to prevent errors if the function is undefined
    }
  };

  const onMouseLeave = (e: React.MouseEvent) => {
    if (hovered) {
      setHovered(false);
      _onMouseLeave?.(e);
    }
  };

  const onFocus = (e: React.FocusEvent | React.MouseEvent) => {
    if (isMouseEvent(e)) {
      onMouseEnter(e as React.MouseEvent);
    }
  };

  const onBlur = (e: React.FocusEvent | React.MouseEvent) => {
    if (isMouseEvent(e)) {
      onMouseLeave(e as React.MouseEvent);
    } else {
      setHovered(false);
    }
  };

  const onKeyDownAccessibilityButton = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (!hovered) {
        setHovered(true);
        _onMouseEnter?.({ ...e, currentTarget: e.currentTarget?.parentNode });
      }
      if (hovered) {
        setHovered(false);
        _onMouseLeave?.({ ...e, currentTarget: e.currentTarget?.parentNode });
      }
    }
  };
  const onKeyDownMenuItem = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (hovered) {
        setHovered(false);
      }
      _onMouseLeave?.(e);
    }
  };
  // TODO: Improve this previous state polyfill until we're more familiar with the implications on the custom element
  const containerType = isContainer ? 'drop' : 'menu';
  const hasLink =
    link &&
    (link.hasOwnProperty('href') ||
      link.hasOwnProperty('target') ||
      link.hasOwnProperty('rel') ||
      link.hasOwnProperty('linkPopupId'));
  const state = [
    containerType,
    isSelected && 'selected',
    isHovered && 'over',
    hasLink ? 'link' : 'header',
  ];

  const dataAttributes = {
    ...getDataAttributes(props),
    'data-direction': direction,
    'data-listposition': positionInList,
    'data-parent-id': parentId,
    'data-data-id': dataId,
    'data-state': state.join(' '),
    'data-index': index,
    'data-dropdown': isDropDownButton,
  };

  const _getLabel = (_label?: string) =>
    _label ? _label.trim() : EMPTY_STRING;

  // for SEO purposes, render list of subitems, visible to bots only
  const subItemList = hasSubItems ? (
    <ul aria-hidden={true} style={{ display: 'none' }}>
      {subItems.map((subItem, i) => {
        const { hasPopup, 'aria-current': ariaCurrent } = subItem;
        return (
          <li key={subItem.id || i}>
            <MenuButtonLink
              wrapperProps={{
                ariaHasPopup: hasPopup,
                ariaCurrent,
              }}
              link={subItem.link}
              tabIndex={-1}
              compClassName={compClassName}
            >
              {_getLabel(subItem.label)}
            </MenuButtonLink>
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <TagName
      id={id}
      {...dataAttributes}
      className={classnames(compClassName, className)}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDownMenuItem}
    >
      {children(_getLabel(label))}
      {hasSubItems && (
        <button
          className={classnames(styles.srOnly, {
            [styles.rotated]: hovered,
          })}
          onKeyDown={onKeyDownAccessibilityButton}
          aria-label={
            props?.translations?.dropdownButtonAriaLabel?.replace(
              '<%= itemName %>',
              _getLabel(label),
            ) || A11Y_DROPDOWN_CHEVRON_BUTTON_ARIA_LABEL_DEFAULT
          }
        >
          <AccessibilityIcon />
        </button>
      )}
      {subItemList}
    </TagName>
  );
};

export default MenuButtonRoot;
